import { IndianRupee, ScrollText, X, Eye, FileDown } from "lucide-react";
import { useState } from "react";
import * as XLSX from "xlsx";

export default function AffiliateInformation({
  profile,
  formatCurrency,
  paidCommissionForDisplay,
  payouts = [], // Dynamic payouts data
}) {
  const [viewModal, setViewModal] = useState(null);

  const handleDownloadExcel = (payout) => {
    const ws = XLSX.utils.json_to_sheet([
      {
        Invoice: payout.invoice,
        "Payment Mode": payout.mode,
        Amount: payout.amount,
        Date: payout.date,
      },
    ]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Payout Detail");
    XLSX.writeFile(wb, `${payout.invoice}-payout.xlsx`);
  };

  return (
    <>
      {/* Commission Summary */}
      <div className="gap-6 mb-6">
        <div className="bg-[#131313] border border-[#333333] p-4 sm:p-6 rounded-lg">
          <h3 className="text-lg font-bold mb-4 flex items-center">
            <IndianRupee className="text-[#FFCA20] mr-2" />
            Commission Summary
          </h3>

          <div className="flex flex-col sm:flex-row justify-between border border-[#333333] rounded-lg overflow-hidden">
            <div className="flex-1 text-center p-4 sm:p-6 sm:px-7 border-b sm:border-b-0 sm:border-r border-[#333333]">
              <p className="text-[#F3F3F3] text-xs">TOTAL EARNINGS</p>
              <p className="text-xl font-bold">
                ₹{profile.performance?.totalCommission || 0}
              </p>
            </div>
            <div className="flex-1 text-center p-4 sm:p-6 sm:px-10 border-b sm:border-b-0 sm:border-r border-[#333333]">
              <p className="text-[#F3F3F3] text-xs">PAID COMMISSION</p>
              <p className="text-xl font-bold">₹{paidCommissionForDisplay}</p>
            </div>
            <div className="flex-1 text-center p-4 sm:p-6">
              <p className="text-[#F3F3F3] text-xs">PENDING PAYMENT</p>
              <p className="text-xl font-bold">
                ₹{profile.performance?.pendingCommission || 0}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Payout History */}
      <div className="bg-[#131313] border border-[#333333] p-6 rounded-lg">
        <h3 className="text-lg font-bold mb-4 flex items-center">
          <ScrollText className="text-[#FFCA20] mr-2" />
          Pay-out History
        </h3>

        <div className="overflow-x-auto scrollbar-hide rounded-lg border border-[#333333]">
          <table className="w-full text-sm text-left overflow-hidden rounded-lg">
            <thead className="text-gray-400 bg-[#1a1a1a] divide-x divide-[#333333]">
              <tr>
                <th className="py-2 px-3 rounded-tl-lg border-r border-[#333333]">
                  INVOICE ID
                </th>
                <th className="py-2 px-3 border-x border-[#333333]">
                  PAYMENT MODE
                </th>
                <th className="py-2 px-3 border-x border-[#333333]">AMOUNT</th>
                <th className="py-2 px-3 border-x border-[#333333]">DATE</th>
                <th className="py-2 px-3 rounded-tr-lg">ACTIONS</th>
              </tr>
            </thead>
            <tbody className="text-white bg-[#1a1a1a] divide-y divide-[#333333]">
              {payouts.length > 0 ? (
                payouts.map((payout, index) => {
                  const isLast = index === payouts.length - 1;
                  return (
                    <tr key={payout.id || index} className="divide-x divide-[#333333]">
                      <td className={`py-3 px-3 ${isLast ? "rounded-bl-lg" : ""}`}>
                        {payout.invoice}
                      </td>
                      <td className="py-3 px-3">{payout.mode}</td>
                      <td className="py-3 px-3">{payout.amount}</td>
                      <td className="py-3 px-3">{payout.date}</td>
                      <td
                        className={`py-3 px-3 ${isLast ? "rounded-br-lg" : ""}`}
                      >
                        <div className="flex space-x-4 text-sm">
                          <button
                            onClick={() => setViewModal(payout)}
                            className="text-white hover:underline flex items-center"
                          >
                            <Eye className="w-4 h-4 mr-1" /> View
                          </button>
                          <button
                            onClick={() => handleDownloadExcel(payout)}
                            className="text-[#FFCA20] hover:underline flex items-center"
                          >
                            <FileDown className="w-4 h-4 mr-1" /> Download
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="5" className="py-8 px-3 text-center text-gray-400 rounded-b-lg">
                    No data to show for this period
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* View Modal */}
      {viewModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
          <div className="bg-[#131313] border border-[#333333] rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-white">Payout Details</h3>
              <button
                onClick={() => setViewModal(null)}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-3 text-white">
              <div className="flex justify-between">
                <span className="text-gray-400">Invoice:</span>
                <span>{viewModal.invoice}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Payment Mode:</span>
                <span>{viewModal.mode}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Amount:</span>
                <span className="text-[#FFCA20] font-bold">{viewModal.amount}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Date:</span>
                <span>{viewModal.date}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
